import FSMState from '@src/slide/infrastructure/module/independent/state-machine/fsm-state';
import { BOOT_STATE_TYPE } from '../boot-manager.interface';
import TaskManager, { TaskPriority } from '@src/slide/infrastructure/module/dependent/taskManager';
import ua from '@tencent/docs-user-agent';
import { tdw } from '@src/slide/infrastructure/module/dependent/report/offline-data-report/tdw-report';
import {
    TDWActionType,
    TDWModuleType,
    TDWOperationType,
} from '@src/slide/infrastructure/module/dependent/report/offline-data-report/constant';
import { KVR } from '@tencent/tencent-doc-report';
import { getUrlParameter } from '@src/slide/infrastructure/module/independent/url/param-operation';
import { AppServiceProvider } from '@src/slide/application/app-service-provider';
import { IPcEditorAppServices } from '@src/slide/application/entry/toc/editor/pc/pc-editor-app-services.interface';
import { StartPCPlay } from '@src/slide/application/service/remote-control-manager/core/constant';
import { IEditorLifeCycleService, LifeCycleType } from '@src/slide/application/service/editor-lifecycle/core/interface';

export default class PcFirstSlideRenderState extends FSMState {
    public constructor() {
        super(BOOT_STATE_TYPE.firstSlideDidRender);
    }

    public enterCallback() {
        // 重置计时器
        this.duration = 0;
        window.firstScreenBrandLoading?.hide();
        // ssr不可见
        this.hidePlaceholderContainer();

        this.getAppService().viewableServiceLayer.workbenchService.promise().then((workbenchService) => {
            workbenchService.loadWorkBench(document.getElementById('workbench') as HTMLElement);
        });

        this.getAppService().viewableServiceLayer.shortCutKeyManager.promise();

        this.getLifecycleService()?.emitEvent(LifeCycleType.FIRST_SLIDE_RENDER);

        this.getAppService().viewableServiceLayer.notifyManager.promise();

        this.getAppService().viewableServiceLayer.multiSelectedDashOuterManager.promise();

        import(/* webpackChunkName: "service" */ '@src/framework/frame/components/quick-setting/quick-setting-manager')
            .then((module) => {
                const QuickSettingManager = module.default;
                QuickSettingManager.getInstance().initWithStore();
            })
            .catch((error) => {
                window.log.error(`[loadChunk-error]${error}`);
            });
        this.getAppService().viewableServiceLayer.liveManager.promise().then(async (liveManager) => {
            const { LiveWillBegin } = await import(/* webpackChunkName: "service" */ '@tencent/live-components');
            liveManager.event.on(LiveWillBegin, () => {
                // const isFromCurrent = args && args.isFromCurrent;
                this.getAppService().viewableServiceLayer.playManager.playAction?.(true, true, true);
            });
        });
        this.getAppService().viewableServiceLayer.remoteControlManager.promise().then((remoteControlManager) => {
            remoteControlManager.event.on(StartPCPlay, () => {
                this.getAppService().viewableServiceLayer.playManager.playAction?.(true, false, false, false, true);
            });
        });
        this.getAppService().viewableServiceLayer.accessibilityManager.promise().then((a11yManager) => {
            a11yManager.readOpenContent();
        });
        TaskManager.addTaskWithBlock('report-hardware-accelerated', TaskPriority.LOW, () => {
            this.reportHardwareAccelerated();
        });
        this.getAppService().viewableServiceLayer.urlParamManager.promise().then((urlParamManager) => {
            const menuManager = this.getAppService().editableServiceLayer?.menuManager;
            if (menuManager) {
                urlParamManager.init(menuManager);
            }
        });
        this.getAppService().viewableServiceLayer.authGuideManager.promise();
        this.reportCreateFrom();
    }

    /**
     * 首屏渲染完成，隐藏编辑区ssr
     * 此阶段不能直接删除
     * 因为删除会露出下方的真正编辑区，用户就可以操作了，但workbench ready后会将dom节点appendChild到workbench内
     * 重新挂载会让用户的一些行为被重置，比如输入光标、滚动条等
     * 所以在重挂载前只是让其不可见，阻挡用户操作，挂载后再移除
     * @private
     */
    private hidePlaceholderContainer(): void {
        const placeholderContainer = document.getElementById('placeholder-container');
        if (placeholderContainer) {
            placeholderContainer.style.opacity = '0';
        }
    }

    private getLifecycleService(): IEditorLifeCycleService | undefined {
        return this.getAppService().viewableServiceLayer.lifeCycleService;
    }

    private reportHardwareAccelerated(): void {
        const canvas = document.createElement('canvas');
        const gl = canvas.getContext('webgl');

        let accelerated = true;
        if (gl) {
            const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
            if (debugInfo) {
                const renderer = gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL);
                accelerated = renderer.indexOf('SwiftShader') === -1;
            }
        }

        tdw({
            opername: TDWOperationType.DOC_PERFORMANCE,
            module: TDWModuleType.PERFORMANCE_DETAIL,
            action: TDWActionType.PERFORMANCE_WEBGL_SUPPORTED,
            ver6: JSON.stringify({
                support: !!gl,
                accelerated: accelerated ? 1 : 0,
                platform: 'pc',
                userAgent: ua.userAgent,
                device: this.getDeviceName(),
                env: this.getEnv(),
                filterSupport: this.canvasFilterSupport(),
            }),
        });
    }

    // 上报文档创建的入口
    private reportCreateFrom() {
        if (__IS_WECOM__) {
            const createFromConvInfo = {
                conversationId: decodeURIComponent(getUrlParameter('createFromConvId') || ''),
                conversationName: decodeURIComponent(getUrlParameter('createFromConvName') || ''),
            };
            if (createFromConvInfo.conversationId && createFromConvInfo.conversationName) {
                // 避免clientVars为空
                const timeoutId = setTimeout(() => {
                    KVR.oss({
                        itemname: 'ww_conv_create',
                        Channel2: 'slide',
                        biz: 'wedocs',
                    });
                    clearTimeout(timeoutId);
                }, 2000);
            }
        }
    }


    private canvasFilterSupport(): boolean {
        const canvas = document.createElement('canvas');
        const filter = canvas.getContext('2d')?.filter;
        return !!filter && filter.length > 0;
    }

    private getDeviceName(): string {
        let device = 'unknown';
        if (ua.isMac) {
            device = 'Mac';
        } else if (ua.isWindows) {
            device = 'Windows';
        } else if (ua.isLinux) {
            device = 'Linux';
        }
        return device;
    }

    private getEnv(): string {
        const env = this.getBrowser() || this.getApp() || 'unknown';
        return env;
    }

    private getBrowser(): string | null {
        let browser: string | null = null;
        if (ua.isChrome) {
            browser = 'Chrome';
        } else if (ua.isFirefox) {
            browser = 'Firefox';
        } else if (ua.isIE) {
            browser = 'IE';
        } else if (ua.isSafari) {
            browser = 'Safari';
        } else if (ua.isOldEdge) {
            browser = 'OldEdge';
        } else if (ua.isChromiumEdge) {
            browser = 'ChromiumEdge';
        } else if (ua.isQQBrowser) {
            browser = 'QQBrowser';
        } else if (ua.isSouGou) {
            browser = 'SouGou';
        }
        return browser;
    }

    private getApp(): string | null {
        let app: string | null = null;
        if (ua.isQQ) {
            app = 'QQ';
        } else if (ua.isTim) {
            app = 'TIM';
        } else if (ua.isPCTencentDocsClient) {
            app = 'DocsClient';
        } else if (ua.isTencentMeeting) {
            app = 'Meeting';
        } else if (ua.isWeChat) {
            app = 'WeChat';
        } else if (ua.isWxWork) {
            app = 'WxWork';
        } else if (ua.isWxDrive) {
            app = 'WxDrive';
        }
        return app;
    }

    private getAppService(): IPcEditorAppServices {
        return AppServiceProvider.getInstance().getAppServices() as IPcEditorAppServices;
    }
}
